/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef, useState, useEffect, RefObject } from 'react';

import cn from 'classnames';

import { useEventListener } from 'hooks/useEventListener';

import { ProgressLine } from 'ui/atoms/ProgressLine/ProgressLine';

import styles from './ReadingBar.module.scss';

type Props = {
  className?: string;
  scrollArticlesRef: RefObject<HTMLDivElement | null>;
};

export const ReadingBar = ({ className, scrollArticlesRef }: Props) => {
  const scrollThrottle = useRef<NodeJS.Timeout>(undefined);

  const [width, setWidth] = useState<number>(0);
  const [topOffset, setTopOffset] = useState<number>(0);

  useEffect(() => {
    return () => {
      if (scrollThrottle.current !== undefined) {
        clearTimeout(scrollThrottle.current);
      }
    };
  }, []);

  useEffect(() => {
    const header = document.getElementById('header');
    if (!header) return;

    let raf = 0;
    const update = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        setTopOffset(Math.max(0, header.getBoundingClientRect().bottom));
      });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(header);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEventListener('scroll', () => {
    if (scrollThrottle.current !== undefined) {
      return;
    }

    scrollThrottle.current = setTimeout(() => {
      window.requestAnimationFrame(() => {
        scrollThrottle.current = undefined;

        const elem = document.documentElement;

        const scrollTop = elem.scrollTop || document.body.scrollTop;
        const scrollHeight = scrollArticlesRef.current?.scrollHeight || document.body.scrollHeight;
        const percent = (scrollTop / (scrollHeight - elem.clientHeight)) * 100;

        setWidth(percent);
      });
    }, 610);
  });

  return (
    <div className={styles['reading-bar-wrapper']} style={{ top: topOffset }}>
      <ProgressLine className={cn(styles['reading-bar'], className)} percentage={width} />
    </div>
  );
};
